import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";
import "./StockChart.css";

const useResizeObserver = (ref) => {
  const [dimensions, setDimensions] = useState(null);
  useEffect(() => {
    const observeTarget = ref.current;
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setDimensions(entry.contentRect);
      });
    });
    resizeObserver.observe(observeTarget);
    return () => {
      resizeObserver.unobserve(observeTarget);
    };
  }, [ref]);

  return dimensions;
};

function StockChart({
  data = {},
  deltaData = {},
  dimensions = {},
  labels = {},
}) {
  const svgRef = useRef();
  const wrapperRef = useRef();
  const wrapDim = useResizeObserver(wrapperRef);
  const curve = d3.curveLinear;

  let {
    width,
    height,
    margin = {},
    chartHeight,
    matchedDeltaHeight,
    focusHeight,
  } = dimensions;

  const svgHeight = height;

  let filteredData = [...data];
  let filteredBarData = [...deltaData];
  let brush;
  let xBrush;
  const domainFactor = 1.1;

  function filterMinute(minutes) {
    const svg = d3.select(svgRef.current);

    if (!minutes) {
      svg.select(".brush-group").call(brush.move, null);
    } else {
      let endDate = xBrush.domain()[1];
      //let fromDate = d3.timeHour.offset(endDate, -hours);
      let fromDate = d3.timeMinute.offset(endDate, -minutes);

      svg.select(".brush-group").call(
        brush.move,
        [fromDate, endDate].map((d) => xBrush(d))
      );
    }
  }

  function bisect(data, date) {
    const bisectDate = d3.bisector((d) => d.datetime).left;
    const i = bisectDate(data, date, 1);
    const a = data[i - 1],
      b = data[i];
    if (b == null) return a;
    return date - a.date > b.date - date ? b : a;
  }

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    if (!wrapDim) return;

    let width = wrapDim.width;

    svg.selectAll("*").remove();
    svg.attr("height", svgHeight).attr("width", width);

    let lastFilteredData = filteredData;

    // console.log(filteredBarData)

    // const spreadDomain = [0, d3.min(filteredData, d => d.spread) * domainFactor];
    const maxSpread = d3.max(filteredData, (d) => d.spread);
    const minSpread = d3.min(filteredData, (d) => d.spread);
    const spreadDomain = [
      maxSpread * (2 - domainFactor),
      minSpread * domainFactor,
    ];

    const x = d3
      .scaleUtc()
      .domain(d3.extent(filteredData, (d) => d.datetime))
      .range([margin.left, width - margin.right]);
    // .nice()

    const hoverX = d3
      .scaleBand()
      .domain(d3.range(filteredData.length))
      .range([margin.left, width - margin.right])
      .padding(0);

    const y = d3
      .scaleLinear()
      .domain(spreadDomain)
      .range([chartHeight - margin.bottom, margin.top]);

    // const xScaleMatchedDelta = d3
    //     .scaleBand()
    //     .domain(d3.range(filteredBarData.length))
    //     .range([margin.left, width - margin.right])
    //     .padding(0.1)

    const yScaleMatchedDelta = d3
      .scaleLinear()
      // const yScaleMatchedDelta = d3.scaleLog()
      // .domain([0.1, d3.max(filteredBarData, (d) => d.change)])
      .domain([0, d3.max(filteredBarData, (d) => d.change)])
      .range([matchedDeltaHeight * 0.8, 0]);

    const area = d3
      .area()
      .defined((d) => !isNaN(d.spread))
      .x((d) => x(d.datetime))
      .y0(y.range()[0])
      .y1((d) => y(d.spread))
      .curve(curve);

    const line = d3
      .line()
      .defined((d) => !isNaN(d.spread))
      .x((d) => x(d.datetime))
      .y((d) => y(d.spread))
      .curve(curve);

    var matchedDeltaGroup = svg
      .append("g")
      .attr("transform", `translate(0,${chartHeight + 45})`);

    const yAxisDelta = d3
      .axisRight(yScaleMatchedDelta)
      .tickFormat(d3.format("$,"))
      .tickValues([
        0,
        yScaleMatchedDelta.domain()[1] / 2,
        yScaleMatchedDelta.domain()[1],
      ]);
    const yAxisDeltaInnerGroup = (g) =>
      g
        .attr("transform", `translate(${width - margin.right},0)`)
        .call(yAxisDelta.ticks(4))
        .call((g) => g.select(".domain").remove())
        .call((g) =>
          g
            .selectAll(".title")
            .data(["Change"])
            .join("text")
            .attr("class", "title")
            .attr("x", -margin.left)
            .attr("dx", -10)
            .attr("y", -5)
            .attr("fill", "black")
            .attr("text-anchor", "start")
            .text((d) => d)
        );

    const yAxisDeltaGroup = matchedDeltaGroup
      .append("g")
      .call(yAxisDeltaInnerGroup);

    // var matchedDeltaLabelGroup = svg
    //   .append("g")
    //   .attr("class", "rect-labels")
    //   .attr(
    //     "transform",
    //     `translate(0,${chartHeight + yScaleMatchedDelta.range()[0]})`
    //   );

    // var matchedDeltaUnderline = matchedDeltaLabelGroup
    //   .append("line")
    //   .attr("stroke", "white")
    //   .attr("stroke-width", 6);

    // var matchedDeltaLabel = matchedDeltaLabelGroup
    //   .append("text")
    //   .attr("font-family", "sans")
    //   .attr("text-anchor", "middle")
    //   .attr("font-weight", 700)
    //   // .attr('fill', 'steelblue')
    //   .attr("dy", 12)
    //   .attr("font-size", 10);

    matchedDeltaGroup
      .selectAll("rect")
      .data(filteredBarData)
      .enter()
      .append("rect")
      .attr("class", (d, i) => `delta-bar delta-bar-${i}`)
      .attr("fill", (d, i) => {
        const moneyLine = bisect(filteredData, d.datetime);
        let moneyLineOther;
        const index = filteredData.indexOf(moneyLine);

        if (moneyLine.datetime >= d.datetime) {
          moneyLineOther = filteredData[index - 1];
        } else {
          moneyLineOther = filteredData[index + 1];
        }

        var moneyLineItems = [moneyLineOther, moneyLine].sort(
          (a, b) => a.datetime.getTime() - b.datetime.getTime()
        );
        moneyLineItems = moneyLineItems.map((d) =>
          d ? Math.abs(d.spread) : null
        );

        if (!moneyLineItems[0] || !moneyLineItems[1]) return "grey";
        else if (moneyLineItems[0] < moneyLineItems[1]) return "green";
        else return "red";
      })
      .attr("x", (d) => x(d.datetime) - 10)
      .attr("width", (d, i) => {
        let deltaWidth =
          i < filteredBarData.length - 2
            ? x(filteredBarData[i + 1].datetime) -
              x(filteredBarData[i].datetime)
            : Math.abs(x.range()[1] - x(filteredBarData[i].datetime));

        if (deltaWidth < 2) deltaWidth = 2;
        return deltaWidth;
      })
      .attr("y", (d) => (d.change == 0 ? 0 : yScaleMatchedDelta(d.change)))
      .attr("height", (d) =>
        d.change == 0
          ? 0
          : yScaleMatchedDelta(0.1) - yScaleMatchedDelta(d.change)
      )
      .append("title")
      .text((d) => d.change);

    //lines
    // matchedDeltaGroup.selectAll('line')
    //     .data(filteredBarData)
    //     .enter()
    //     .append('line')
    //     .attr('x1', (d, i) => x(d.datetime))
    //     .attr('x2', (d, i) => x(d.datetime))
    //     .attr('y1', 0)
    //     .attr('y2', yScaleMatchedDelta(0))
    //     .attr('stroke-width', 2)
    //     .attr('stroke', 'black')

    const mainChartGroup = svg.append("g");

    const pathArea = mainChartGroup
      .append("path")
      .datum(filteredData)
      .attr("class", "area")
      .attr("fill", "#E2F2FB")
      .attr("opacity", 1)
      .attr("d", area);

    const lineChart = mainChartGroup
      .append("path")
      .datum(filteredData)
      .attr("stroke", "steelblue")
      .attr("stroke-width", 2)
      .attr("fill", "none")
      .attr("opacity", 0.75)
      .attr("d", line);

    let hoveredIndex;

    mainChartGroup
      .append("rect")
      .attr("class", "hover-rect")
      .attr("width", width)
      .attr("fill", "red")
      .attr("opacity", 0.0)
      .attr("height", chartHeight)
      .on("mousemove", function (event, d) {
        const dataItem = bisect(filteredData, x.invert(event.offsetX));
        updateHover(dataItem);
      })
      .on("mouseout", function (event, d) {
        clearHover();
        hoveredIndex = -1;
      });

    const brushGroup = svg
      .append("g")
      .attr(
        "transform",
        `translate(0,${chartHeight + matchedDeltaHeight + 20})`
      );

    const yBrush = d3
      .scaleLinear()
      .range([focusHeight, 0])
      .domain(spreadDomain);
    xBrush = d3
      .scaleUtc()
      .domain(d3.extent(data, (d) => d.datetime))
      .range([margin.left, width - margin.right]);
    const xAxisBrush = (g) =>
      g
        .attr("transform", `translate(0,${focusHeight})`)
        .call(
          d3
            .axisBottom(x)
            .ticks(width / 80)
            .tickSizeOuter(0)
        )
        .attr("class", "brush x axis");

    brushGroup.append("g").call(xAxisBrush);

    const brushArea = d3
      .area()
      .x((d) => x(d.datetime))
      .y0(focusHeight)
      .y1((d) => yBrush(d.spread))
      .curve(curve);

    brushGroup
      .append("path")
      .datum(data)
      .attr("class", "brush area")
      .attr("d", brushArea);

    brush = d3
      .brushX()
      .handleSize(10)
      .extent([
        [margin.right, -10],
        [width - margin.left, focusHeight],
      ])
      .on("end", (e) => brushed(e, true)); //this fires only when releasing the mouse

    const yAxis = d3.axisRight(y);
    const yAxisInnerGroup = (g) =>
      g
        .attr("transform", `translate(${width - margin.right},0)`)
        .call(
          yAxis.tickValues([maxSpread, (maxSpread + minSpread) / 2, minSpread])
        )
        .call((g) => g.select(".domain").remove())
        .call((g) =>
          g
            .selectAll(".title")
            .data(["Spread"])
            .join("text")
            .attr("class", "title")
            .attr("x", -margin.left)
            .attr("dx", -10)
            .attr("y", 10)
            .attr("fill", "black")
            .attr("text-anchor", "start")
            .text("Spread")
        );

    const xAxis = d3
      .axisBottom(x)
      .ticks(width / 80)
      .tickSizeOuter(0);
    const xAxisInnerGroup = (g) =>
      g
        .attr("transform", `translate(0,${chartHeight - margin.bottom})`)
        .call(xAxis);

    const xAxisGroup = svg.append("g").call(xAxisInnerGroup);
    const yAxisGroup = svg.append("g").call(yAxisInnerGroup);
    const brushComponent = brushGroup
      .append("g")
      .attr("class", "brush-group")
      .call(brush);

    let hoverGroup = mainChartGroup.append("g");
    // .attr('transform', `translate(${margin.left},0)`)

    let hoverLineVertical = hoverGroup
      .append("line")
      .attr("class", "hover-line");

    let hoverLineHorizontal = hoverGroup
      .append("line")
      .attr("class", "hover-line");

    let hoverCircle = hoverGroup
      .append("circle")
      .attr("r", 4)
      .attr("stroke", "steelblue")
      .attr("stroke-width", 2)
      .attr("fill", "white")
      .attr("class", "hover-circle")
      .attr("opacity", 0);

    const labelWidth = 80;
    const labelHeight = 40;

    let hoverDateGroup = hoverGroup.append("g").attr("opacity", 0);
    hoverDateGroup
      .append("rect")
      .attr("fill", "steelblue")
      .attr("width", labelWidth)
      .attr("height", labelHeight)
      .attr("rx", 5);
    // .attr("opacity", 0);

    // let hoverDateGroupText = hoverDateGroup.append('text')
    //     .attr('x', labelWidth / 2)
    //     .attr('y', 15)
    //     .attr("font-family", "sans")
    //     .attr('font-size', 8)
    //     .attr("text-anchor", "middle")
    //     .attr("fill", "white");

    let hoverDateGroupText = hoverDateGroup
      .append("foreignObject")
      .attr("width", labelWidth)
      .attr("height", labelHeight)
      .attr("font-family", "sans")
      .attr("font-size", 9)
      .attr("text-anchor", "middle")
      .attr("color", "white")
      .attr("x", 0)
      .attr("y", 10);

    function updateHover(hoveredItem) {
      if (hoveredItem == null) return;

      let xOffset = x(hoveredItem.datetime);

      hoverLineVertical
        .attr("opacity", 0.5)
        .attr("x1", xOffset)
        .attr("x2", xOffset)
        .attr("y1", margin.top)
        .attr("y2", chartHeight - margin.bottom);

      hoverLineHorizontal
        .attr("opacity", 0.5)
        .attr("x1", margin.left)
        .attr("x2", width - margin.right)
        .attr("y1", y(hoveredItem.spread))
        .attr("y2", y(hoveredItem.spread));

      hoverCircle
        .attr("opacity", 1)
        .attr("cx", xOffset)
        .attr("cy", y(hoveredItem.spread))
        .raise();

      const index = filteredData.indexOf(hoveredItem);

      // const halfBandwidth = xScaleMatchedDelta.bandwidth() / 2;
      hoveredIndex = index;

      // matchedDeltaLabel
      //     .attr('opacity', 1)
      //     .attr('x', xScaleMatchedDelta(index) + halfBandwidth)
      //     .text(hoveredItem.total_matched_delta)

      // matchedDeltaUnderline
      //     .attr('opacity', 1)
      //     .attr('x1', xScaleMatchedDelta(index))
      //     .attr('x2', xScaleMatchedDelta(index) + halfBandwidth * 2)
      //     .attr('y1', -2)
      //     .attr('y2', -2)

      hoverDateGroup
        .attr("opacity", 1)
        .attr(
          "transform",
          `translate(${xOffset - labelWidth / 2},${
            chartHeight - margin.top - labelHeight * 1.2
          })`
        );
      hoverDateGroupText.text(
        `${d3.timeFormat("%b %d, %Y")(hoveredItem.datetime)}
                Spread: ${d3.format(".1f")(hoveredItem.spread)}`
      );
    }

    function clearHover() {
      hoverLineVertical.attr("opacity", 0);
      hoverLineHorizontal.attr("opacity", 0);
      hoverCircle.attr("opacity", 0);
      hoverDateGroup.attr("opacity", 0);
      // matchedDeltaLabel.attr("opacity", 0);
      // matchedDeltaUnderline.attr("opacity", 0);
    }

    function formatDate(date) {
      return d3.timeFormat("%Y %B %d, %H:%M")(date);
    }

    function brushed(event, automatic) {
      var brushedDateFrom, brushedDateTo;

      if (automatic) {
        var selection = event.selection || xBrush.range();
        var selectedDates = selection.map(xBrush.invert);
        brushedDateFrom = selectedDates[0];
        brushedDateTo = selectedDates[1];
      } else {
        //manual call (predefined time periods)
        brushedDateTo = data.slice(-1)[0].date;
      }

      filteredData = data.filter(
        (d) =>
          d.datetime.getTime() >= brushedDateFrom &&
          d.datetime.getTime() <= brushedDateTo
      );
      filteredBarData = deltaData.filter(
        (d) =>
          d.datetime.getTime() >= brushedDateFrom &&
          d.datetime.getTime() <= brushedDateTo
      );

      x.domain(d3.extent(filteredData, (d) => d.datetime));
      y.domain([0, d3.min(filteredData, (d) => d.spread) * domainFactor]);
      // yScaleMatchedDelta.domain([0, d3.max(filteredBarData, d => d.change) * domainFactor])

      xAxis.scale(x);
      yAxis.scale(y);
      yAxisDelta.scale(yScaleMatchedDelta);

      d3.selectAll(".delta-bar").remove();

      matchedDeltaGroup
        .selectAll("rect")
        .data(filteredBarData)
        .enter()
        .append("rect")
        .attr("class", (d, i) => `delta-bar delta-bar-${i}`)
        .attr("fill", (d, i) => {
          const moneyLine = bisect(filteredData, d.datetime);
          let moneyLineOther;
          const index = filteredData.indexOf(moneyLine);

          if (moneyLine.datetime >= d.datetime) {
            moneyLineOther = filteredData[index - 1];
          } else {
            moneyLineOther = filteredData[index + 1];
          }

          var moneyLineItems = [moneyLineOther, moneyLine].sort(
            (a, b) => a.datetime.getTime() - b.datetime.getTime()
          );
          moneyLineItems = moneyLineItems.map((d) =>
            d ? Math.abs(d.spread) : null
          );

          if (!moneyLineItems[0] || !moneyLineItems[1]) return "grey";
          else if (moneyLineItems[0] < moneyLineItems[1]) return "green";
          else return "red";
        })
        .attr("x", (d) => x(d.datetime) - 10)
        .attr("width", (d, i) => {
          let deltaWidth =
            i < filteredBarData.length - 2
              ? x(filteredBarData[i + 1].datetime) -
                x(filteredBarData[i].datetime)
              : Math.abs(x.range()[1] - x(filteredBarData[i].datetime));

          if (deltaWidth < 2) deltaWidth = 2;
          return deltaWidth;
        })
        .attr("y", (d) => (d.change == 0 ? 0 : yScaleMatchedDelta(d.change)))
        .attr("height", (d) => {
          if (yScaleMatchedDelta(0.1) - yScaleMatchedDelta(d.change) < 0)
            console.log(d);
          return d.change == 0
            ? 0
            : yScaleMatchedDelta(0.1) - yScaleMatchedDelta(d.change);
        })
        .append("title")
        .text((d) => d.change);

      xAxisGroup.transition().duration(250).call(xAxis);
      yAxisGroup.transition().duration(250).call(yAxis);
      yAxisDeltaGroup.transition().duration(250).call(yAxisDelta);
      // yAxisGroup.transition().duration(250).call(yAxisInnerGroup);
      // yAxisInnerGroup.transition().duration(250).call(yAxis);

      pathArea.datum(filteredData).attr("d", area);

      lineChart.datum(filteredData).attr("d", line);
    }
  }, [wrapDim]);

  return (
    <div
      ref={wrapperRef}
      style={{
        width: "80%",
        minHeight: svgHeight,
        maxHeight: svgHeight,
        // margin: '2rem'
      }}
      className="chart-wrapper"
    >
      <div className="filter-area">
        <a className="filter-item" href="#" onClick={() => filterMinute(1)}>
          1m
        </a>
        <a className="filter-item" href="#" onClick={() => filterMinute(5)}>
          5m
        </a>
        <a className="filter-item" href="#" onClick={() => filterMinute(10)}>
          10m
        </a>
        <a className="filter-item" href="#" onClick={() => filterMinute(15)}>
          15m
        </a>
        <a className="filter-item" href="#" onClick={() => filterMinute(60)}>
          1h
        </a>
        <a
          className="filter-item"
          href="#"
          onClick={() => filterMinute(60 * 2)}
        >
          2h
        </a>
        <a
          className="filter-item"
          href="#"
          onClick={() => filterMinute(60 * 3)}
        >
          3h
        </a>
        <a
          className="filter-item"
          href="#"
          onClick={() => filterMinute(60 * 6)}
        >
          6h
        </a>
        <a
          className="filter-item"
          href="#"
          onClick={() => filterMinute(60 * 24 * 1)}
        >
          1d
        </a>
        <a
          className="filter-item"
          href="#"
          onClick={() => filterMinute(60 * 24 * 3)}
        >
          3d
        </a>
        <a
          className="filter-item"
          href="#"
          onClick={() => filterMinute(60 * 24 * 7)}
        >
          1w
        </a>
        <a className="filter-item" href="#" onClick={() => filterMinute(null)}>
          all
        </a>
      </div>
      <svg ref={svgRef}></svg>
    </div>
  );
}

export default StockChart;
